import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationApplyTargetValue,
	GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';

export type BeneficiaryReconciliationPreflightOrganizationResult =
	| {
			action:
				| 'CREATE_SUB'
				| 'IGNORE_ITEM'
				| 'RESUME_PREFLIGHT_ROW'
				| 'TREAT_AS_MATRIX';
			cnpj?: string | null;
			onHold?: boolean;
	  }
	| {
			removePending: true;
	  };

interface PreflightOrganizationModalData {
	group: GenericBeneficiaryReconciliationUnidentifiedOrganizationGroup;
	pendingChange?: {
		action: string;
		targetValue: BeneficiaryReconciliationApplyTargetValue;
	} | null;
}

@Component({
	selector: 'app-beneficiary-reconciliation-preflight-organization-modal',
	styleUrls: [
		'./beneficiary-reconciliation-preflight-organization-modal.component.scss',
	],
	templateUrl:
		'./beneficiary-reconciliation-preflight-organization-modal.component.html',
})
export class BeneficiaryReconciliationPreflightOrganizationModalComponent {
	constructor(
		@Inject(MAT_DIALOG_DATA) readonly data: PreflightOrganizationModalData,
		private readonly dialogRef: MatDialogRef<
			BeneficiaryReconciliationPreflightOrganizationModalComponent,
			BeneficiaryReconciliationPreflightOrganizationResult
		>,
	) {}

	get canCreateSub(): boolean {
		return this.data.group.canCreateSub;
	}

	get canResume(): boolean {
		return this.data.group.canResume;
	}

	get canTreatAsMatrix(): boolean {
		return this.data.group.canTreatAsMatrix;
	}

	get identifierLabel(): string {
		if (this.isSubinvoiceNotFound) {
			const subinvoiceNumber =
				this.data.group.subinvoiceNumber ||
				this.data.group.normalizedSubinvoiceNumber;

			return subinvoiceNumber ? `Subfatura ${subinvoiceNumber}` : 'Subfatura -';
		}

		return this.data.group.cnpj || '-';
	}

	get identifierTitle(): string {
		return this.isSubinvoiceNotFound ? 'Subfatura informada' : 'CNPJ informado';
	}

	get introText(): string {
		return this.isSubinvoiceNotFound
			? 'Não encontramos esta subfatura no sistema. Decida qual ação tomar.'
			: 'Não encontramos este CNPJ no sistema ou ele não pertence ao contexto selecionado. Decida qual ação tomar.';
	}

	get issueLabelList(): string[] {
		const labels = this.data.group.issueCodes.map((code) =>
			this.getIssueLabel(code),
		);

		return Array.from(new Set(labels));
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onCreateSub(): void {
		this.dialogRef.close({
			action: 'CREATE_SUB',
			cnpj: this.data.group.cnpj,
		});
	}

	onIgnore(): void {
		this.dialogRef.close({ action: 'IGNORE_ITEM' });
	}

	onPlaceOnHold(): void {
		this.dialogRef.close({ action: 'IGNORE_ITEM', onHold: true });
	}

	onRemove(): void {
		this.dialogRef.close({ removePending: true });
	}

	onResume(): void {
		this.dialogRef.close({ action: 'RESUME_PREFLIGHT_ROW' });
	}

	onTreatAsMatrix(): void {
		this.dialogRef.close({ action: 'TREAT_AS_MATRIX' });
	}

	private getIssueLabel(code: string): string {
		switch (code) {
			case 'CNPJ_NOT_REGISTERED':
			case 'UNKNOWN_CNPJ':
				return 'CNPJ não cadastrado';
			case 'CNPJ_OUTSIDE_CONTEXT':
				return 'CNPJ fora do contexto';
			case 'MISSING_CNPJ':
				return 'CNPJ ausente';
			case 'SUBINVOICE_NOT_FOUND':
				return 'Número da subfatura não encontrado';
			default:
				return code;
		}
	}

	private get isSubinvoiceNotFound(): boolean {
		return this.data.group.issueCodes.includes('SUBINVOICE_NOT_FOUND');
	}
}
